import React, { useEffect, useState } from "react";
import ProfileBasicInfo from "../../candidate/ProfileBasicInfo";
import CandidateDashboardNav from "../../candidate/CandidateDashboardNav";
import CreateProfile from "../../candidate/CreateProfile";
import EditProfile from "../../candidate/EditProfile";
import axios from "axios";
import Breadcrumb from "../../Breadcrumb";


const CVUpload = ({ userId, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

    if (selectedFile && allowedTypes.includes(selectedFile.type)) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a valid CV file (PDF or Word document)');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('cv', file);
    formData.append('userId', userId);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}candidate_details/upload-cv`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSuccess(true);
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload CV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cv-upload-container">
      <div className="mb-3">
        <input
          type="file"
          className="form-control"
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx"
          disabled={loading}
        />
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          CV uploaded successfully!
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={handleUpload}
        disabled={!file || loading}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Uploading...
          </>
        ) : (
          'Upload CV'
        )}
      </button>
    </div>
  );
};

const CVDownload = ({ cvFilename }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleDownload = async () => {
    if (!cvFilename) {
      setError('No CV file available');
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}candidate_details/download-cv/${cvFilename}`,
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = cvFilename; 
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to download CV. Please try again.');
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="cv-download-container">
      {error && (
        <div className="alert alert-danger mb-2" role="alert">
          {error}
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={handleDownload}
        disabled={downloading || !cvFilename}
      >
        {downloading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Downloading...
          </>
        ) : (
          <>
            <i className="fa-solid fa-download me-2"></i>
            Download CV
          </>
        )}
      </button>
    </div>
  );
};


const CandidateProfile = () => {
  const [profileData, setProfileData] = useState(null);
  const [fullProfile, setFullProfile] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCVUpload, setShowCVUpload] = useState(false);

  const userId = localStorage.getItem("userid");

  const fetchFullUserData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}candidate_details/showcandidatebyid/${userId}`
      );
      const fullProfileData = response.data;

      if (fullProfileData) {
        setProfileData(fullProfileData);
        setFullProfile(fullProfileData);
      }
    } catch (error) {
      if(error.response && error.response.status === 404){
        setProfileData(null);
      }
      else{
        console.error("Error finding candidate detail:", error);
        setError("Failed to load detailed profile data. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFullUserData();
  }, [userId]);

  // if (loading) {
  //   return (
  //     <div className="d-flex justify-content-center align-items-center my-4">
  //       <div className="spinner-border text-primary" role="status">
  //         <span className="visually-hidden">Loading...</span>
  //       </div>
  //     </div>
  //   );
  // }

  // Error message
  // if (error) {
  //   return (
  //     <div className="alert alert-danger text-center my-4" role="alert">
  //       {error}
  //     </div>
  //   );
  // }

  return (
    <>
      <Breadcrumb
        title="My Profile"
        path={[
          { label: "Home", href: "/", active: false },
          { label: "My Profile", active: true }
        ]}
      />
      <div className="user-profile py-120">
        <div className="container">
          <div className="row">
            <CandidateDashboardNav />
            <div className="col-lg-9">
            {error ? (
              <div className="alert alert-danger text-center my-4" role="alert">
                {error}
              </div>
            ) : (
            loading ? (
              <div className="d-flex justify-content-center align-items-center my-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
            profileData ? (
              editMode ? (
                <EditProfile
                  profileData={profileData}
                  fullProfile={fullProfile}
                  onSave={() => setEditMode(false)}
                />
              ) : (
                <>
                  <ProfileBasicInfo
                    profileData={profileData}
                    fullProfile={fullProfile}
                  />
                  <div className="d-flex justify-content-center mt-3 gap-2">
                    <button
                      className="btn btn-warning"
                      onClick={() => {
                        setEditMode(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                      }}
                    >
                      <i className="fa-solid fa-edit me-1"></i> Edit Profile
                    </button>
                    {profileData.uploadCV ? (
                      <>
                        <CVDownload cvFilename={profileData.uploadCV} />
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => setShowCVUpload(!showCVUpload)}
                        >
                          <i className="fa-solid fa-upload me-1"></i>
                          Update CV
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => setShowCVUpload(!showCVUpload)}
                      >
                        <i className="fa-solid fa-upload me-1"></i>
                        Upload CV
                      </button>
                    )}
                  </div>
                  {showCVUpload && (
                    <div className="mt-4">
                      <CVUpload
                        userId={userId}
                        onUploadSuccess={() => {
                          fetchFullUserData();
                          setShowCVUpload(false);
                        }}
                      />
                    </div>
                  )}
                </>
              )
            ) : (
              <CreateProfile />
            )
          )
        )}
        </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default CandidateProfile;
